/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: RingGroupSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Call Center module.
----------------------------------------------------------------*/

import { ChangeEvent } from 'react';
import { Users } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { FormBox } from '../../components/ui/FormBox';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import Toggle from '../../components/ui/Toggle';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import ModuleDestinationSelect from '../../components/ui/ModuleDestinationSelect';
import RecordingSelect from '../../components/ui/RecordingSelect';
import { CallerIdInput } from '../../components/ui/CallerIdInput';

type Strategy = 'simultaneous' | 'sequence' | 'enterprise' | 'random' | 'rollover';

interface FormState {
  name: string;
  extension: string;
  strategy: Strategy;
  ringTimeout: string;
  fallbackModule: string;
  fallbackValue: string;
  callerIdName: string;
  callerIdNumber: string;
  greeting: string;
  confirmKey: string;
  confirmFile: string;
  skipBusy: boolean;
  enabled: boolean;
}

interface RingGroupSectionProps {
  formData: FormState;
  onFieldChange: <K extends keyof FormState>(field: K, value: FormState[K]) => void;
  onStrategyChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  onFallbackModuleChange: (value: string) => void;
  onFallbackValueChange: (value: string) => void;
}

const STRATEGY_OPTIONS: Strategy[] = [
  'simultaneous',
  'sequence',
  'enterprise',
  'random',
  'rollover'
];

export function RingGroupSection({
  formData,
  onFieldChange,
  onStrategyChange,
  onFallbackModuleChange,
  onFallbackValueChange
}: RingGroupSectionProps) {
  const { t } = useI18n();

  return (
    <FormBox title={t('ringGroups.form.sections.general')} icon={<Users className="h-5 w-5" />}>
      <div className="grid grid-cols-12 gap-3">
        {/* Line 1: Extension | Group Name */}
        <SimpleTooltip content={t('ringGroups.form.fields.extension.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="extension" className="text-left">
            {t('ringGroups.form.fields.extension.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="extension"
            value={formData.extension}
            onChange={(event) => onFieldChange('extension', event.target.value)}
            placeholder={t('ringGroups.form.fields.extension.placeholder')}
          />
        </div>

        <SimpleTooltip content={t('ringGroups.form.fields.name.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="name" className="text-left">
            {t('ringGroups.form.fields.name.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="name"
            value={formData.name}
            onChange={(event) => onFieldChange('name', event.target.value)}
            placeholder={t('ringGroups.form.fields.name.placeholder')}
            required
          />
        </div>

        {/* Line 2: Strategy | Caller ID Prefix */}
        <SimpleTooltip content={t('ringGroups.form.fields.strategy.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="strategy" className="text-left">
            {t('ringGroups.form.fields.strategy.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <select
            id="strategy"
            value={formData.strategy}
            onChange={onStrategyChange}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {STRATEGY_OPTIONS.map((strategy) => (
              <option key={strategy} value={strategy}>
                {t(`ringGroups.strategy.${strategy}`)}
              </option>
            ))}
          </select>
        </div>

        <SimpleTooltip content={t('ringGroups.form.fields.callerIdPrefix.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="callerIdPrefix" className="text-left">
            {t('ringGroups.form.fields.callerIdPrefix.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <CallerIdInput
            id="callerIdPrefix"
            className="w-full"
            nameValue={formData.callerIdName}
            numberValue={formData.callerIdNumber}
            onNameChange={(value) => onFieldChange('callerIdName', value)}
            onNumberChange={(value) => onFieldChange('callerIdNumber', value)}
            namePlaceholder={t('ringGroups.form.fields.callerIdPrefix.namePlaceholder')}
            numberPlaceholder={t('ringGroups.form.fields.callerIdPrefix.numberPlaceholder')}
            nameTooltip={t('ringGroups.form.fields.callerIdPrefix.nameTooltip')}
            numberTooltip={t('ringGroups.form.fields.callerIdPrefix.numberTooltip')}
          />
        </div>

        {/* Line 3: Greeting | Ring Timeout (sec) */}
        <SimpleTooltip content={t('ringGroups.form.fields.greeting.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="greeting" className="text-left">
            {t('ringGroups.form.fields.greeting.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <RecordingSelect
            value={formData.greeting}
            onChange={(value) => onFieldChange('greeting', value)}
            placeholder={t('ringGroups.form.fields.greeting.placeholder')}
            returnType="id"
            showPlayButton
          />
        </div>

        <SimpleTooltip content={t('ringGroups.form.fields.ringTimeout.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="ringTimeout" className="text-left">
            {t('ringGroups.form.fields.ringTimeout.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="ringTimeout"
            type="number"
            min={1}
            max={3600}
            value={formData.ringTimeout}
            onChange={(event) => onFieldChange('ringTimeout', event.target.value)}
            placeholder={t('ringGroups.form.fields.ringTimeout.placeholder')}
          />
        </div>

        {/* Line 4: Confirm Prompt Recording | Confirm Key */}
        <SimpleTooltip content={t('ringGroups.form.fields.confirmFile.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="confirmFile" className="text-left">
            {t('ringGroups.form.fields.confirmFile.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <RecordingSelect
            value={formData.confirmFile}
            onChange={(value) => onFieldChange('confirmFile', value)}
            placeholder={t('ringGroups.form.fields.confirmFile.placeholder')}
            returnType="id"
            showPlayButton
          />
        </div>

        <SimpleTooltip content={t('ringGroups.form.fields.confirmKey.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="confirmKey" className="text-left">
            {t('ringGroups.form.fields.confirmKey.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="confirmKey"
            value={formData.confirmKey}
            onChange={(event) => onFieldChange('confirmKey', event.target.value)}
            placeholder={t('ringGroups.form.fields.confirmKey.placeholder')}
          />
        </div>

        {/* Line 5: Timeout Destination (ModuleDestinationSelect - two selects) */}
        <SimpleTooltip content={t('ringGroups.form.fields.fallbackDestination.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label className="text-left">
            {t('ringGroups.form.fields.fallbackDestination.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-9">
          <ModuleDestinationSelect
            moduleValue={formData.fallbackModule === 'none' ? '' : formData.fallbackModule}
            destinationValue={formData.fallbackValue}
            onModuleChange={onFallbackModuleChange}
            onDestinationChange={onFallbackValueChange}
            modulePlaceholder={t('ringGroups.form.fields.fallbackDestination.modulePlaceholder')}
            destinationPlaceholder={t('ringGroups.form.fields.fallbackDestination.destinationPlaceholder')}
            className="w-full"
          />
        </div>

        {/* Line 6: Skip Busy Members | Enabled */}
        <SimpleTooltip content={t('ringGroups.form.fields.skipBusy.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="skipBusy" className="text-left">
            {t('ringGroups.form.fields.skipBusy.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Toggle
            id="skipBusy"
            value={formData.skipBusy}
            onChange={(value) => onFieldChange('skipBusy', value)}
          />
        </div>

        <SimpleTooltip content={t('ringGroups.form.fields.enabled.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="enabled" className="text-left">
            {t('ringGroups.form.fields.enabled.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Toggle
            id="enabled"
            value={formData.enabled}
            onChange={(value) => onFieldChange('enabled', value)}
          />
        </div>
      </div>
    </FormBox>
  );
}


